import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createCrmLead, getWarehouses } from '../config/api';
import useIsMobile from '../hooks/useIsMobile';

const emptyContact = () => ({
  name: '',
  phone: '',
  designation: '',
  isPrimary: false,
  doNotCall: false,
  noWhatsapp: false,
});

const CreateCrmLead = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [displayName, setDisplayName] = useState('');
  const [gst, setGst] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [hubId, setHubId] = useState('');
  const [hubs, setHubs] = useState([]);
  const [loadingHubs, setLoadingHubs] = useState(true);
  const [contacts, setContacts] = useState([{ ...emptyContact(), isPrimary: true }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadHubs = async () => {
      try {
        const response = await getWarehouses();
        setHubs(response?.data || []);
      } catch (error) {
        console.error('Failed to load hubs:', error);
      } finally {
        setLoadingHubs(false);
      }
    };
    loadHubs();
  }, []);

  const updateContact = (index, field, value) => {
    setContacts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const setPrimary = (index) => {
    setContacts((prev) => prev.map((c, i) => ({ ...c, isPrimary: i === index })));
  };

  const addContact = () => setContacts((prev) => [...prev, emptyContact()]);

  const removeContact = (index) => {
    setContacts((prev) => {
      const next = prev.filter((_, i) => i !== index);
      // Keep a primary if we removed it and contacts remain.
      if (next.length && !next.some((c) => c.isPrimary)) next[0].isPrimary = true;
      return next.length ? next : [{ ...emptyContact(), isPrimary: true }];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanedContacts = contacts
      .map((c) => ({ ...c, phone: (c.phone || '').replace(/\D/g, '').slice(-10) }))
      .filter((c) => c.phone);

    if (!displayName.trim() && cleanedContacts.length === 0) {
      alert('Enter a lead name or at least one contact with a phone number.');
      return;
    }

    const invalid = cleanedContacts.find((c) => c.phone.length !== 10);
    if (invalid) {
      alert('Each contact phone must be a valid 10-digit number.');
      return;
    }

    const payload = {
      displayName: displayName.trim(),
      gst: gst.trim(),
      tags: tagsText
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      contacts: cleanedContacts,
      ...(hubId && { hubId: Number(hubId) }),
    };

    setSaving(true);
    try {
      const res = await createCrmLead(payload);
      const newId = res?.data?.id;
      alert('Lead created.');
      if (newId) navigate(`/crm/leads/${newId}`);
      else navigate('/crm/leads');
    } catch (err) {
      // Backend returns a message naming the holding lead on a duplicate.
      alert(err.message || 'Failed to create lead.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div className="order-detail-header">
          <div className="order-detail-title">
            <h2 style={{ margin: 0 }}>Add lead</h2>
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
              A phone or GST can belong to only one lead.
            </div>
          </div>
          <div className="order-actions">
            <Link to="/crm/leads" className="btn btn-secondary">← Back</Link>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="filters" style={{ marginTop: '8px' }}>
            <div className="filter-group">
              <label className="form-label">Lead name</label>
              <input
                type="text"
                className="form-control"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Business or person name"
              />
            </div>
            <div className="filter-group">
              <label className="form-label">GST (optional)</label>
              <input
                type="text"
                className="form-control"
                value={gst}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => setGst(e.target.value)}
                placeholder="GST number"
              />
            </div>
            <div className="filter-group">
              <label className="form-label">Hub (optional)</label>
              <select
                className="form-control"
                value={hubId}
                onChange={(e) => setHubId(e.target.value)}
                disabled={loadingHubs || saving}
              >
                <option value="">{loadingHubs ? 'Loading hubs…' : 'No hub selected'}</option>
                {hubs.map((hub) => (
                  <option key={hub.id} value={hub.id}>
                    {hub.name}{hub.city ? ` — ${hub.city}${hub.state ? `, ${hub.state}` : ''}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="form-label">Tags (comma separated)</label>
              <input
                type="text"
                className="form-control"
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                placeholder="e.g. UP, high-priority"
              />
            </div>
          </div>

          <h3 className="section-title" style={{ marginTop: '16px' }}>Contacts</h3>
          {contacts.map((c, index) => (
            <div
              key={index}
              style={{
                border: '1px solid #eee',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '10px',
              }}
            >
              <div className="filters">
                <div className="filter-group">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={c.name}
                    onChange={(e) => updateContact(index, 'name', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label className="form-label">Phone (10 digits)</label>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    className="form-control"
                    value={c.phone}
                    onChange={(e) => updateContact(index, 'phone', e.target.value)}
                    placeholder="9876543210"
                  />
                </div>
                <div className="filter-group">
                  <label className="form-label">Designation</label>
                  <input
                    type="text"
                    className="form-control"
                    value={c.designation}
                    onChange={(e) => updateContact(index, 'designation', e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '12px', display: 'flex', gap: '5px', alignItems: 'center' }}>
                  <input
                    type="radio"
                    name="primaryContact"
                    checked={c.isPrimary}
                    onChange={() => setPrimary(index)}
                  />
                  Primary
                </label>
                <label style={{ fontSize: '12px', display: 'flex', gap: '5px', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={c.doNotCall}
                    onChange={(e) => updateContact(index, 'doNotCall', e.target.checked)}
                  />
                  Do not call
                </label>
                <label style={{ fontSize: '12px', display: 'flex', gap: '5px', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={c.noWhatsapp}
                    onChange={(e) => updateContact(index, 'noWhatsapp', e.target.checked)}
                  />
                  No WhatsApp
                </label>
                {contacts.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    style={{ padding: isMobile ? '8px 12px' : '2px 10px', fontSize: isMobile ? '13px' : '12px', marginLeft: 'auto' }}
                    onClick={() => removeContact(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}

          <button type="button" className="btn btn-secondary" onClick={addContact}>
            + Add another contact
          </button>

          <div className={isMobile ? 'm-sticky-actions' : undefined} style={{ marginTop: '20px', display: 'flex', gap: '8px' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Create lead'}
            </button>
            <Link to="/crm/leads" className="btn btn-secondary">Cancel</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCrmLead;
